import type { ArchiveDraft } from './personArchive';
import type { Vendor } from './lists.types';

export interface DashboardStats {
  activitiesCount: number;
  departmentsCount: number;
  proceduresCount: number;
  usersCount: number;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface QuickLink {
  id: string;
  title: string;
  url: string;
  icon?: string;
  description?: string;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  url: string;
  description?: string;
  // When set by an admin, the action always appears on every user's dashboard
  // and cannot be removed from their selection
  alwaysShow?: boolean;
}

export interface DashboardConfig {
  stats: DashboardStats;
  announcements: Announcement[];
  quickLinks: QuickLink[];
  quickActions: QuickAction[];
  myQuickActions: string[];
  announcementPreviewLength: number;
  userDepartments: Array<{
    id: string;
    departmentCode: string;
    departmentName: string;
  }>;
  user: {
    id: string;
    name: string;
    userRole: string;
    department: string;
    departmentName: string;
  };
}

export interface PaginationState {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ActivitiesSearchResult {
  success: boolean;
  activities: Activity[];
  pagination: PaginationState;
}

export type Department = {
  id: string;
  departmentCode: string;
  departmentName: string;
  departmentManager?: string | User;
  isActive?: boolean;
  archived?: boolean;
  deleted?: boolean;
  archivedBy?: string;
  archivedOn?: string;
  createdAt?: Date;
  updatedAt?: Date;
  users?: User[];
  procedures?: Procedure[];
};

export const jobRoleTitles = [
  'Test',
  'Accountant',
  'Accounts Executive',
  'Accounts Payable Officer',
  'Accounts Receivable Officer',
  'AI Engineer',
  'Allocator',
  'Backend Engineer',
  'Brand Manager',
  'Branch Manager',
  'Business Development Manager',
  'Business Development Representative',
  'Bookkeeper',
  'Cadet',
  'Chief Executive Officer',
  'Chief Marketing Officer',
  'Chief Operations Officer',
  'Commercial Director',
  'Commercial Manager',
  'Commercial Officer',
  'Commercial Team Leader',
  'Construction Manager',
  'Contracts & Claims Manager',
  'Contracts Administrator',
  'Credit & Collections Officer',
  'Chief Revenue Officer',
  'Chief Sales & Revenue Officer',
  'Chief Technology Officer',
  'Customer Success Manager',
  'Designer',
  'Draftsman',
  'Estimator',
  'Finance Manager',
  'Front End Engineer',
  'Full Stack Engineer',
  'General Manager',
  'Hire Manager',
  'HR Manager',
  'HSEQ Manager',
  'Implementation Manager',
  'Inventory Controller',
  'IT Intern',
  'IT Manager',
  'Labour Manager',
  'Labourer',
  'Leading Hand',
  'Managing Director',
  'Marketing Manager',
  'Office Admin',
  'Office Manager',
  'Operations Admin',
  'Operations Coordinator',
  'Operations Director',
  'Operations Manager',
  'Paralegal',
  'Partner',
  'Payroll Officer',
  'President',
  'Principal',
  'Procurement Manager',
  'Progress Claims Manager',
  'Progress Claims Officer',
  'Project Coordinator',
  'Project Manager',
  'Project Engineer',
  'QA Engineer',
  'Receptionist',
  'Regional Manager',
  'Safety Officer',
  'Sales Assistant',
  'Sales Development Rep',
  'Sales Manager',
  'Sales Representative',
  'Sales Team Leader',
  'Scaffolder',
  'Site Manager',
  'Site Supervisor',
  'Site Engineer',
  'Site Foreman',
  'State Manager',
  'Structural Engineer',
  'Team Leader',
  'Tech Lead',
  'Technician',
  'Tender Manager',
  'Transport Manager',
  'Truck Driver',
  'Vice President',
  'Warehouse Assistant',
  'Warehouse Manager',
  'Yard Supervisor',
  'Yardsman'
] as const;

export type JobRoleTitle = string;

export interface JobTitle {
  id: string;
  name: string;
  deleted?: boolean;
  deletedOn?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type JobRoleLogEntry = {
  id?: string;
  timestamp: string;
  userId: string;
  changeDescription: string;
};

export type JobRole = {
  id: string;
  title: JobRoleTitle;
  roleName: JobRoleTitle;
  description: string;
  departmentId: string;
  department?: {
    id: string;
    departmentCode: string;
    departmentName: string;
  };
  outcomes: string[];
  keyDuties: string[];
  activityLogs?: ActivityLogEntry[];
  attachments?: Attachment[];
  archived?: boolean;
  archivedOn?: string;
  archivedBy?: string;
  archivedReason?: string;
  requiredCertificatesAndLicenses?: QualificationPreset[];
  preferredQualifications?: QualificationPreset[];
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  jobGradeId?: string | null;
  jobGrade?: {
    id: string;
    name: string;
    salaryRangeMin?: number;
    salaryRangeMax?: number;
  };
  // department?: Department; // only if you use the optional populated object
};

export interface JobGrade {
  id: string;
  name: string;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  deleted?: boolean;
  deletedOn?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type JobPosition = {
  id: string;
  positionCode: string;
  jobRoleId: string;
  jobRole?: JobRole;
  departmentId: string;
  department?: Department;
  location: string;
  reportsTo?: {
    type: 'User' | 'Position';
    id: string;
    name: string;
  } | null;
  assignedUser?: {
    id: string;
    name: string;
  } | null;
  employmentType: string;
  minSalary?: number;
  maxSalary?: number;
  startDate: string;
  endDate?: string;
  status: 'Filled' | 'Vacant';
  businessJustification: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export interface JobRoleLocation {
  id: string;
  name: string;
  deleted?: boolean;
  deletedOn?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type UserRole = string;

export interface CorrespondenceAttachment {
  id: number;
  filename: string;
  key: string;
  url: string;
  size: number;
  mimetype: string;
  uploadedAt: string;
  uploadedBy: User;
}

export interface Correspondence {
  id: number;
  type: "Verbal Warning" | "Written Warning" | "Coaching Advice" | "General Feedback" | "File Notes" | "Private File Notes";
  message: string;
  tags: string[];
  isActive: boolean;
  deleted: boolean;
  deletedAt?: string;
  archived: boolean;
  archivedOn?: string;
  createdAt: string;
  updatedAt: string;
  employee: User;
  author: User;
  parent?: Correspondence;
  replies?: Correspondence[];
  archivedBy?: User;
  attachments?: CorrespondenceAttachment[];
  requiresAcknowledgement?: boolean;
  acknowledged?: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: User;
  remindersSent?: number;
  lastReminderDate?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  userRole: UserRole;
  jobRole: JobRoleTitle;
  department: string | Department;  // Primary department
  departments?: string[];  // All departments including primary
  mobile?: string;
  manager?: string | null;
  isActive: boolean;
  status?: string; // 'Active' | 'Onboarding'
  deleted: boolean;
  deletedAt?: string;
  archived?: boolean;
  archivedBy?: string;
  archivedOn?: string;
  /** Set by the Archive Person journey when the person is archived. */
  terminationDate?: string | null;
  terminationReason?: string | null;
  /** Present while an archive journey is in progress (drives the profile CTA). */
  archiveDraft?: ArchiveDraft | null;
  password?: string;
  lastLogin?: Date;
  attachments?: Attachment[];
  createdAt: Date;
  updatedAt: Date;
  userManager?: User;
  dateOfBirth?: string;
  startDate?: string;
  skills?: { name: string; category: string }[];
  interests?: { name: string; category: string }[];
  activityLogs?: ActivityLogEntry[];
  showInAttendance?: boolean;
  accrualStartDate?: string;
  isTemporary?: boolean;
  temporaryDepartment?: string;
  employmentStatus?: string;
  qualificationGroups?: QualificationGroup[];
  hourlyRate?: string | number;
  baseSalaryRate?: string | number;
  commissionsBenefits?: string;
  packageInclusions?: Record<string, boolean>;
  otherPackageInclusion?: string;
  employmentEntity?: EmploymentEntity | number | null;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emergencyContactRelationship?: string;
  residentialAddress?: string;
  gender?: string | null;
  indigenousStatus?: string | null;
  countryOfBirth?: string | null;
  disabilityStatus?: string | null;
  workLocation?: string | null;
  isSubcontractor?: boolean | null;
  /** Sent on update; the API returns the joined relation instead. */
  subcontractorVendorId?: number | null;
  subcontractorVendor?: Vendor | null;
}

export interface Approvers {
  id: string;
  email: string;
  name: string;
  userRole: UserRole;
}

export interface EmploymentEntity {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NewUser {
  email: string;
  password: string;
  name: string;
  userRole: UserRole;
  jobRole: JobRoleTitle;
  department: string;
  mobile?: string;
  manager?: string | null;
  dateOfBirth?: string;
  startDate?: string;
  showInAttendance?: boolean;
  employmentStatus?: string;
  employmentEntity?: number | null;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emergencyContactRelationship?: string;
  residentialAddress?: string;
  gender?: string | null;
  indigenousStatus?: string | null;
  countryOfBirth?: string | null;
  disabilityStatus?: string | null;
  workLocation?: string | null;
  isSubcontractor?: boolean | null;
  subcontractorVendorId?: number | null;
}

export interface Attachment {
  id: string;
  filename: string;
  key: string;
  url: string;
  size: number;
  mimetype: string;
  uploadedBy: string | User;
  uploadedAt: string;
  // Present only on locally-staged attachments (deferred upload before the
  // parent resource exists); never persisted to the server.
  file?: File;
}

export type ProcedureLink = {
  id: string;
  title: string;
  url: string;
  description?: string;
};

export type ProcedureLogEntry = {
  id: string;
  timestamp: string;
  user: string | User;
  changeDescription: string;
  // _id?: string;
};
export type RaciRole = 'responsiblePerson' | 'accountablePerson' | 'consultedPerson' | 'informedPerson';

export const PartyType = {
  User: 'User',
  JobRole: 'JobRole',
  ExternalPartner: 'ExternalPartner',
} as const;

export type PartyType = (typeof PartyType)[keyof typeof PartyType];

export interface RaciParty {
  id: number | string;
  type: PartyType;
  name?: string;
  title?: string;
  roleName?: string;
}

export type ActivityRaci = {
  responsiblePerson?: RaciParty;
  accountablePerson?: RaciParty;
  consultedPerson?: RaciParty[];
  informedPerson?: RaciParty[];
  // [K in RaciRole]?: RaciParty;
}

export type ActivitiesData = {
  success: boolean;
  activities: Activity[];
  pagination: PaginationState;
}

export interface Activity {
  id: string;
  activityName: string;
  description?: string;
  procedure: string | Procedure;
  sequence: number;
  assignedTo?: string;
  status?: string;
  dependencies?: Array<{
    activity: string;
    type: string;
  }>;
  dueDate?: string;
  responsiblePerson: string | RaciParty;
  accountablePerson: string | RaciParty;
  consultedPerson?: string | RaciParty | null;
  informedPerson?: string | RaciParty | null;
  howTo?: string;
  systemsUsed?: string[];
  links?: ActivityLink[];
  activityLogs?: ActivityLogEntry[];
  attachments?: Array<{
    id: string;
    filename: string;
    key: string;
    url: string;
    size: number;
    mimetype: string;
    uploadedBy: string;
    uploadedAt: string;
  }>;
  createdBy?: string | User;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  _swapWithActivityId?: string;
  raci?: ActivityRaci;
}

export interface ActivityLink {
  id: string;
  title: string;
  url: string;
  description?: string;
}

export interface ProcedureVersion {
  version: number;
  changes: string;
  changedBy: string | User;
  changedOn: string;
}

export interface Procedure {
  id: string;
  procedureName: string;
  whyImportant?: string;
  howToComplete?: string;
  deleted?: boolean;
  deletedOn?: string;
  deletedBy?: string | User;
  procedureCadence: string;
  procedureOwner: string | User;
  procedureStakeholders: Array<string | User>;
  viewers: Array<string | User>;
  departmentId: string;
  department: Department | null;
  createdBy: string | User;
  createdAt: string;
  lastModifiedBy?: string | User;
  lastModifiedOn?: string;
  archived?: boolean;
  archivedBy?: string | User;
  archivedOn?: string;
  archivedReason?: string;
  description?: string;
  videoEmbed?: string;
  systems?: System[] | string[];
  links?: ProcedureLink[];
  activities: Activity[];
  attachments?: Attachment[];
  versions?: ProcedureVersion[];
  currentVersion?: number;
  activityLogs?: ActivityLogEntry[];
}

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  user: string | User;
  changeDescription: string;
}

export interface ActivityChange {
  id: string;
  timestamp: string;
  changedBy: string | User;
  field: string;
  oldValue: string;
  newValue: string;
}

export type System = {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | User;
};

export type TrainingStatus = 'Not Started' | 'In Progress' | 'Completed';

export type TrainingType = 'Procedure' | 'Activity';

export interface TrainingLogEntry {
  id: string;
  timestamp: string;
  userId: string;
  changeDescription: string;
}

export type TrainingLink = {
  id: string;
  name: string;
  url: string;
  description?: string;
};

export interface ProcedureCheck {
  explained?: boolean;
  demonstrated?: boolean;
  explainedDate?: string;
  demonstratedDate?: string;
  traineeNotes?: string;
  trainingPlan: TrainingPlan;
  procedure: Procedure;
}

export interface Training {
  id: string;
  itemId: string;
  itemType: 'Procedure' | 'Activity';
  userId: string;
  trainerUserId?: string;
  status: TrainingStatus;
  startDate?: string;
  completedDate?: string;
  createdOn: string;
  notes?: string;
  acknowledgedShown: boolean;
  acknowledgedDemonstrated: boolean;
  links?: TrainingLink[];
  procedureChecks: {
    [key: string]: ProcedureCheck;
  };
  activityLog?: ActivityLogEntry[];
}

export type NewDepartment = {
  departmentCode: string;
  departmentName: string;
  departmentManager: string;
};

export type TrainingPlanStatus = 'Not Started' | 'In Progress' | 'Completed';

export type TrainingPlan = {
  id: string;
  trainingPlanName: string;
  description: string;
  procedures: Procedure[]; // Array of procedure IDs
  traineeId: string; // The user being trained
  trainee: User;
  trainerId?: string; // The person conducting the training
  trainer?: User;
  sourceTemplateId?: string; // ID of the template this plan was created from
  sourceTemplate?: TrainingPlanTemplate;
  status: TrainingPlanStatus;
  createdAt: string;
  startedOn?: string;
  completedOn?: string;
  createdBy: User; // The user who created the plan
  activityLog?: ActivityLogEntry[];
  // procedureChecks?: {
  //   [procedureId: string]: ProcedureCheck;
  // };
  procedureChecks: ProcedureCheck[];
  archived?: boolean;
  archivedOn?: string | null;
  archivedBy?: string | User | null;
  archivedReason?: string;
  deleted?: boolean;
  deletedOn?: string | null;
  deletedBy?: string | User | null;
};

export interface ActivityUpdateResponse {
  success: boolean;
  activity: Activity;
  procedure: Procedure;
  activities: Activity[];
}

declare module 'aria-query';
declare module 'html-minifier-terser';
declare module '@testing-library/jest-dom';
declare module 'd3-geo'; // or replace with correct name if it's not 'dager'

// Add the ExternalPartner type
export interface ExternalPartner {
  id: string;
  name: string;
  active: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

// Policy Management Types
export interface Policy {
  id: string;
  title: string;
  policyNumber?: string;
  description: string;
  status: 'Draft' | 'Active' | 'Archived';
  content: string;
  fileUrl?: string;
  requireSignature: boolean;
  effectiveDate: string;
  expiryDate?: string;
  reviewDate?: string;
  approvedBy?: string;
  version: number;
  previousVersions: Array<{
    version: number;
    changes: string;
    changedBy: string;
    changedOn: string;
  }>;
  createdBy: User;
  createdAt: string;
  updatedAt: string;
  activityLogs?: ActivityLogEntry[];
  attachments?: Attachment[];
  archived?: boolean;
  archivedBy?: string | { id: string; name: string; email: string };
  archivedOn?: string;
  archivedReason?: string;
  deleted?: boolean;
  deletedBy?: string;
  deletedOn?: string;
  assignments?: PolicyAssignment[];
}

export interface Memo {
  id: string;
  number: string;
  subject: string;
  status: 'Draft' | 'Active' | 'Archived';
  content: string;
  requireSignature: boolean;
  effectiveDate: string;
  expiryDate?: string;
  approvedBy?: string;
  version: number;
  previousVersions: Array<{
    version: number;
    changes: string;
    changedBy: string;
    changedOn: string;
  }>;
  attachments: Array<{
    id: string;
    filename: string;
    key: string;
    url: string;
    size: number;
    mimetype: string;
    uploadedBy: string;
    uploadedAt: string;
    caption?: string;
  }>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  activityLogs?: ActivityLogEntry[];
  archived: boolean;
  archivedBy?: string;
  archivedOn?: string;
  archivedReason?: string;
  deleted: boolean;
  deletedBy?: string;
  deletedOn?: string;
  assignments?: MemoAssignment[];
}

export interface PolicySignature {
  id: string;
  policyId: string;
  policyVersion: number;
  userId: string;
  signatureType: 'Draw' | 'Type';
  signatureData?: string; // Base64 for drawn, text for typed
  signedDate: string;
  ipAddress: string;
  userAgent: string;
}

export interface PolicyAssignment {
  id: string;
  policy: Policy | null;
  user: User | null;
  assignedDate: string;
  readDate?: string;
  remindersSent: number;
  status?: 'Assigned' | 'Completed';
  assignmentPriority?: 'Low' | 'Med' | 'High' | 'Critical';
  policyVersion: number; // Track which version this assignment is for
  signatureType?: 'Draw' | 'Type';
  signatureData?: string; // Base64 for drawn, text for typed
  signedDate?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt?: string;
}

export interface MemoSignature {
  id: string;
  memoId: string;
  memoVersion?: number; // Optional to support legacy signatures without version
  userId: string;
  signatureType: 'Draw' | 'Type';
  signatureData?: string; // Base64 for drawn, text for typed
  signedDate: string;
  ipAddress: string;
  userAgent: string;
}

export interface MemoAssignment {
  id: string;
  memo: Memo | null;
  user: User | null;
  assignedDate: string;
  readDate?: string;
  remindersSent: number;
  assignmentPriority?: 'Low' | 'Med' | 'High' | 'Critical';
  memoVersion: number;
  // signature?: {
  //   id: string;
  //   signatureType: 'Draw' | 'Type';
  //   signatureData?: string; // Base64 for drawn, text for typed
  //   signedDate: string;
  //   ipAddress: string;
  //   userAgent: string;
  // };
  signatureType?: 'Draw' | 'Type';
  signatureData?: string; // Base64 for drawn, text for typed
  signedDate?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt?: string;
}

export enum PersonType {
  user = "User",
  externalPartner = "ExternalPartner",
  jobRole = "jobRole"
}

export interface Document {
  id?: string;
  number?: string;
  subject?: string;
  title?: string;
  status?: 'Draft' | 'Active' | 'Archived';
  content?: string;
  description?: string;
  requireSignature?: boolean;
  effectiveDate?: string;
  expiryDate?: string;
  approvedBy?: string;
  version?: number;
  fileUrl?: string;
  previousVersions?: Array<{
    version: number;
    changes: string;
    changedBy: string;
    changedOn: string;
  }>;
  attachments?: Attachment[];
  createdBy?: string | User;
  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  archivedBy?: string | User | { id: string; name: string; email: string; };
  archivedOn?: string;
  archivedReason?: string;
  deleted?: boolean;
  deletedBy?: string;
  deletedOn?: string;
  assignment?: MemoAssignment | PolicyAssignment;
  documentType: 'policy' | 'memo';
}

export interface AssignmentUser {
  id: string;
  name: string;
  email: string;
  department: Department | string;
  jobRole: string;
  assignedDate: string;
  readDate?: string;
  signedDate?: string;
  status: 'Assigned' | 'Read' | 'Signed' | 'Overdue';
}

export interface AssignmentDetails {
  id: string; // Assignment ID
  documentId: string; // Policy/Memo ID
  name: string;
  type: 'Policy' | 'Memo';
  version?: number;
  number?: string;
  requireSignature: boolean;
  effectiveDate: string;
  documentStatus: string;
  assignmentPriority: 'Low' | 'Med' | 'High' | 'Critical';
  overdueDays?: number;
  createdDate: string;
  dueDate?: string;
  createdBy: {
    name: string;
    email: string;
  };
  users: AssignmentUser[];
  stats: {
    totalAssigned: number;
    totalRead: number;
    totalSigned: number;
    completionPercentage: number;
  };
  recentActivity: ActivityLogEntry[];
}

export interface CPD {
  id: number;
  title: string;
  type: 'Internal' | 'External';
  points: number;
  cost: number;
  duration: number;
  provider?: string;
  description?: string;
  completionDate: string;
  workerRating?: number;
  managerRating?: number;
  workerNotes?: string;
  managerNotes?: string;
  employee: User;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
}

export interface CPDTarget {
  id: number;
  year: number;
  targetPoints: number;
  deadline?: string;
  status: 'Active' | 'Archived';
  notes?: string;
  employee: User;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
}

export type NotificationType =
  | 'training_plan'
  | 'policy'
  | 'memo'
  | 'procedure'
  | 'correspondence'
  | 'inventory'
  | 'development'
  | 'business_system'
  | 'recruitment'
  | 'safety'
  | 'stock'
  | 'general';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  readAt?: string;
  triggeredBy?: User;
  createdAt: string;
}

export interface QualificationType {
  id: string;
  label: string;
  isArchived: boolean;
}

export interface QualificationGroup {
  id: string;
  label: string;
  isArchived: boolean;
  users?: User[];
  createdAt: string;
  updatedAt: string;
}

export interface QualificationDefault {
  id: string;
  defaultRecipients: string[];
  defaultIntervals: number[];
  updatedAt?: string;
}

export interface QualificationPreset {
  id: string;
  name: string;
  issuingAuthority?: string;
  isArchived: boolean;
  type?: QualificationType;
  createdAt?: string;
  updatedAt?: string;
}

export interface Qualification {
  id: string;
  name: string;
  issuingAuthority?: string;
  issueDate?: string;
  expiryDate?: string;
  courseNumber?: string;
  provider?: string;
  contactPhone?: string;
  cost?: number;
  requiresRenewal: boolean;
  reminderIntervals?: number[];
  reminderRecipients?: string[];
  remindersSent?: Record<string, string>;
  isActive: boolean;
  deleted: boolean;
  deletedAt?: string;
  type?: QualificationType;
  preset?: QualificationPreset;
  person: User;
  createdBy?: User;
  attachments?: Attachment[];
  createdAt: string;
  updatedAt: string;
}

export type TrainingPlanTemplate = {
  id: string;
  templateName: string;
  description?: string;
  status: 'Active' | 'Archived';
  jobRoleId?: string;
  jobRole?: JobRole;
  procedures: Procedure[];
  createdBy: User;
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
  archivedOn?: string | null;
  archivedBy?: string | User | null;
  archivedReason?: string;
};

export * from "./direct-debit";
